import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { adminAPI } from "../services/api";
import { IAdmin, ILoginResponse } from "../types";

interface AuthContextType {
  admin: IAdmin | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (adminData: ILoginResponse) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [admin, setAdmin] = useState<IAdmin | null>(() => {
    // Initialize from localStorage on mount
    const savedAdmin = localStorage.getItem("adminUser");
    if (savedAdmin) {
      try {
        return JSON.parse(savedAdmin);
      } catch {
        return null;
      }
    }
    return null;
  });

  const [token, setToken] = useState<string | null>(
    localStorage.getItem("adminToken")
  );
  const [isLoading, setIsLoading] = useState(true);

  const login = async (adminData: ILoginResponse) => {
    try {
      // Store in localStorage
      localStorage.setItem("adminToken", adminData.token);
      localStorage.setItem("adminUser", JSON.stringify(adminData.admin));

      // Update state with NEW values from adminData
      setToken(adminData.token);
      setAdmin(adminData.admin);

      console.log("Login successful:", adminData.admin);
    } catch (error: any) {
      console.error("Login error:", error);
      throw new Error(error?.message || "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    setToken(null);
    setAdmin(null);
  };

  const checkAuth = async () => {
    const savedToken = localStorage.getItem("adminToken");
    const savedAdmin = localStorage.getItem("adminUser");

    if (!savedToken || !savedAdmin) {
      setIsLoading(false);
      logout();
      return;
    }

    try {
      // Parse saved admin data
      const parsedAdmin = JSON.parse(savedAdmin);

      // Verify token is still valid with backend
      await adminAPI.verifyToken();

      // If verification succeeds, use the FULL admin data from localStorage
      // (because verifyToken only returns decoded JWT which lacks name, createdAt, etc.)
      setAdmin(parsedAdmin);
      setToken(savedToken);

    } catch (error) {
      console.error("Token verification failed:", error);
      // If token is invalid, logout
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    admin,
    token,
    isAuthenticated: !!token && !!admin,
    isLoading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
